import { BehaviorSubject } from 'rxjs';

/** Padding del contenuto principale della pagina (ContentWrapper). */
export const CONTENT_PADDING = 24;

/**
 * Costanti di ordinamento per i livelli di stacking degli Affix.
 * Valori più bassi = più vicini al top dello schermo.
 */
export const AffixOrder = {
  /** Menu bar superiore (es. FrontHeaderTopLayout) */
  TOP_BAR: 0,
  /** Header di pagina (es. AffixAdminHeader, FrontTopPage, PageHeading) */
  PAGE_HEADER: 100,
  /** Elementi secondari (es. sidebar, widget list) */
  SECONDARY: 200,
} as const;

export interface IAffixEntry {
  id: string;
  height: number;
  order: number;
  registeredAt: number;
}

/**
 * Snapshot immutabile emesso dal BehaviorSubject ad ogni mutazione
 */
export interface IAffixSnapshot {
  /** Entries ordinate per order (asc), poi per registeredAt (FIFO). */
  sorted: IAffixEntry[];
  /** Somma di tutte le altezze registrate. */
  totalHeight: number;
}

function sortEntries(entries: Map<string, IAffixEntry>): IAffixEntry[] {
  return Array.from(entries.values()).sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.registeredAt - b.registeredAt;
  });
}

function computeTotalHeight(entries: Map<string, IAffixEntry>): number {
  let total = 0;
  for (const e of entries.values()) {
    total += e.height;
  }
  return total;
}

/**
 * Store centralizzato per la gestione degli offset degli Affix.
 *
 * Tiene traccia delle altezze dei componenti Affix con `asGlobalOffset`
 * e calcola automaticamente gli offset cumulativi per lo stacking.
 *
 * Usa rxjs `BehaviorSubject` per notificare i consumer ad ogni mutazione.
 */
export class AffixManagerStore {
  private entries = new Map<string, IAffixEntry>();
  private registrationCounter = 0;

  /** BehaviorSubject che emette uno snapshot ad ogni mutazione. */
  readonly snapshot$ = new BehaviorSubject<IAffixSnapshot>({
    sorted: [],
    totalHeight: 0,
  });

  /** Registra un nuovo affix globale. */
  register(entry: Omit<IAffixEntry, 'registeredAt'>) {
    this.entries.set(entry.id, {
      ...entry,
      registeredAt: this.registrationCounter++,
    });
    this.emit();
  }

  /** Aggiorna l'altezza di un affix registrato. */
  update(id: string, height: number) {
    const existing = this.entries.get(id);
    if (!existing || existing.height === height) return;
    this.entries.set(id, { ...existing, height });
    this.emit();
  }

  /** Rimuove un affix dal registro. */
  unregister(id: string) {
    if (!this.entries.delete(id)) return;
    this.emit();
  }

  private emit() {
    this.snapshot$.next({
      sorted: sortEntries(this.entries),
      totalHeight: computeTotalHeight(this.entries),
    });
  }
}

// Funzioni pure per calcolo offset (usate dai consumer)

/**
 * Somma delle altezze degli entry che precedono quello con l'id dato.
 * Se l'id non viene trovato, restituisce la somma totale di tutte le altezze.
 */
export function computeOffsetTop(sorted: IAffixEntry[], id: string): number {
  let offset = 0;
  for (const entry of sorted) {
    if (entry.id === id) break;
    offset += entry.height;
  }
  return offset;
}

/** Somma delle altezze degli entry con order strettamente minore del valore dato. */
export function computeOffsetByOrder(
  sorted: IAffixEntry[],
  order: number,
): number {
  let offset = 0;
  for (const entry of sorted) {
    if (entry.order >= order) break;
    offset += entry.height;
  }
  return offset;
}
